import { BreakdownCategory, SceneData } from '../services/types';

const CATEGORY_ALIASES: Record<string, string> = {
    CAST: 'CAST',
    CHARACTER: 'CAST',
    CHARACTERS: 'CAST',
    TALENT: 'CAST',
    'CAST MEMBERS': 'CAST',
    EXTRA: 'EXTRAS',
    EXTRAS: 'EXTRAS',
    BACKGROUND: 'EXTRAS',
    'BACKGROUND ACTORS': 'EXTRAS',
    PROP: 'PROPS',
    PROPS: 'PROPS',
    COSTUME: 'COSTUMES',
    COSTUMES: 'COSTUMES',
    VEHICLE: 'VEHICLES',
    VEHICLES: 'VEHICLES',
    STUNT: 'STUNTS',
    STUNTS: 'STUNTS',
    ANIMAL: 'ANIMALS',
    ANIMALS: 'ANIMALS',
    LANGUAGE: 'LANGUAGES',
    LANGUAGES: 'LANGUAGES',
};

// These are screenplay cue qualifiers, not part of a character's identity.
// Only explicit, industry-standard suffixes are removed; names are never
// fuzzy-matched because two similar names may represent different characters.
const CHARACTER_CUE_SUFFIX_RE = /\s*\((?:V\s*\.?\s*O\s*\.?|O\s*\.?\s*S\s*\.?|O\s*\.?\s*C\s*\.?|CONT(?:['’]D|INUED))\)\s*$/iu;

export const normalizeBreakdownDisplayText = (value: string): string =>
    String(value || '')
        .normalize('NFKC')
        .replace(/\s+/gu, ' ')
        .trim();

export const normalizeBreakdownIdentityKey = (value: string): string =>
    normalizeBreakdownDisplayText(value).toUpperCase();

export const normalizeCharacterDisplayName = (value: string): string => {
    let normalized = normalizeBreakdownDisplayText(value);
    let previous = '';
    while (normalized && normalized !== previous) {
        previous = normalized;
        normalized = normalized.replace(CHARACTER_CUE_SUFFIX_RE, '').trim();
    }
    return normalized;
};

export const getCharacterIdentityKey = (value: string): string =>
    normalizeBreakdownIdentityKey(normalizeCharacterDisplayName(value));

export const getCategoryIdentityKey = (value: string): string => {
    const normalized = normalizeBreakdownIdentityKey(value)
        .replace(/[\s_-]+/gu, ' ')
        .trim();
    return CATEGORY_ALIASES[normalized] || normalized;
};

export const getCategoryDisplayName = (value: string): string => {
    const cleaned = normalizeBreakdownDisplayText(value);
    const key = getCategoryIdentityKey(cleaned);
    return CATEGORY_ALIASES[key] || cleaned;
};

export const isCastCategory = (value: string): boolean => getCategoryIdentityKey(value) === 'CAST';

export const isExtrasCategory = (value: string): boolean => getCategoryIdentityKey(value) === 'EXTRAS';

export const getBreakdownItemIdentityKey = (categoryName: string, item: string): string =>
    isCastCategory(categoryName)
        ? getCharacterIdentityKey(item)
        : normalizeBreakdownIdentityKey(item);

export const normalizeBreakdownItems = (categoryName: string, items: string[] = []): string[] => {
    const seen = new Set<string>();
    const normalized: string[] = [];

    for (const rawItem of items) {
        if (typeof rawItem !== 'string' && typeof rawItem !== 'number') continue;
        const display = isCastCategory(categoryName)
            ? normalizeCharacterDisplayName(String(rawItem))
            : normalizeBreakdownDisplayText(String(rawItem));
        const key = getBreakdownItemIdentityKey(categoryName, display);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        normalized.push(display);
    }

    return normalized;
};

export const normalizeBreakdownCategories = (
    categories: BreakdownCategory[] | null | undefined,
): BreakdownCategory[] => {
    const categoryMap = new Map<string, BreakdownCategory>();

    const source = Array.isArray(categories) ? categories : [];
    for (const rawCategory of source) {
        if (!rawCategory || typeof rawCategory !== 'object') continue;
        const categoryKey = getCategoryIdentityKey(rawCategory.name || rawCategory.id || '');
        if (!categoryKey) continue;

        const categoryName = getCategoryDisplayName(rawCategory.name || categoryKey);
        const items = normalizeBreakdownItems(categoryName, Array.isArray(rawCategory.items) ? rawCategory.items : []);
        const existing = categoryMap.get(categoryKey);
        const mergedMetadata = { ...existing?.itemMetadata, ...rawCategory.itemMetadata };
        const metadataFor = (names: string[]) => {
            const pairs = names.map(item => {
                const key = getBreakdownItemIdentityKey(categoryName, item);
                const metadata = mergedMetadata[key];
                if (!metadata || typeof metadata !== 'object') return null;
                const value = {
                    ...(typeof metadata.notes === 'string' && metadata.notes ? { notes: metadata.notes } : {}),
                    ...(typeof metadata.tagId === 'string' && metadata.tagId ? { tagId: metadata.tagId } : {}),
                    ...(metadata.nonSpeaking === true ? { nonSpeaking: true } : {}),
                    ...(metadata.hidden === true ? { hidden: true } : {}),
                };
                return Object.keys(value).length ? [key, value] as const : null;
            }).filter((entry): entry is NonNullable<typeof entry> => entry !== null);
            return pairs.length ? { itemMetadata: Object.fromEntries(pairs) } : {};
        };

        if (existing) {
            const mergedItems = normalizeBreakdownItems(categoryName, [...existing.items, ...items]);
            categoryMap.set(categoryKey, {
                ...existing,
                color: existing.color || rawCategory.color,
                items: mergedItems,
                count: mergedItems.length,
                ...metadataFor(mergedItems),
            });
            continue;
        }

        categoryMap.set(categoryKey, {
            id: normalizeBreakdownDisplayText(rawCategory.id || '') || categoryKey.toLowerCase().replace(/\s+/gu, '_'),
            name: categoryName,
            count: items.length,
            items,
            ...(rawCategory.color ? { color: rawCategory.color } : {}),
            ...metadataFor(items),
        });
    }

    return Array.from(categoryMap.values());
};

export const breakdownCategoriesNeedNormalization = (
    categories: BreakdownCategory[] | null | undefined,
): boolean => {
    if (categories != null && !Array.isArray(categories)) return true;
    const source = categories || [];
    const normalized = normalizeBreakdownCategories(source);
    if (source.length !== normalized.length) return true;

    return source.some((category, index) => {
        const candidate = normalized[index];
        if (!category || typeof category !== 'object') return true;
        const sourceItems = Array.isArray(category.items) ? category.items : [];
        return !candidate
            || category.id !== candidate.id
            || category.name !== candidate.name
            || category.count !== candidate.count
            || category.color !== candidate.color
            || sourceItems.length !== candidate.items.length
            || sourceItems.some((item, itemIndex) => item !== candidate.items[itemIndex]);
    });
};

export const normalizeSceneBreakdown = (scene: SceneData): SceneData => ({
    ...scene,
    categories: normalizeBreakdownCategories(scene.categories),
});

export const mergeBreakdownCategories = (
    ...categorySets: Array<BreakdownCategory[] | null | undefined>
): BreakdownCategory[] => normalizeBreakdownCategories(categorySets.flatMap(categories => categories || []));

export const getSceneCategoryItems = (scene: SceneData, categoryName: string): string[] => {
    const categoryKey = getCategoryIdentityKey(categoryName);
    return normalizeBreakdownCategories(scene.categories)
        .find(category => getCategoryIdentityKey(category.name) === categoryKey)
        ?.items || [];
};

export const getSceneCast = (scene: SceneData): string[] => getSceneCategoryItems(scene, 'CAST');

export interface BreakdownElementIdentity {
    key: string;
    itemKey: string;
    categoryKey: string;
    name: string;
    category: string;
}

export interface BreakdownElementIndex {
    elements: BreakdownElementIdentity[];
    elementKeysByScene: ReadonlyMap<SceneData, ReadonlySet<string>>;
}

/**
 * Normalize each scene once and retain the canonical element keys used by
 * presence-heavy projections such as a DOOD matrix.
 */
export const buildBreakdownElementIndex = (
    scenes: SceneData[],
    includeCategory: (category: BreakdownCategory) => boolean = () => true,
): BreakdownElementIndex => {
    const elements = new Map<string, BreakdownElementIdentity>();
    const elementKeysByScene = new Map<SceneData, ReadonlySet<string>>();

    for (const scene of scenes) {
        const sceneElementKeys = new Set<string>();
        for (const category of normalizeBreakdownCategories(scene.categories)) {
            if (!includeCategory(category)) continue;
            const categoryKey = getCategoryIdentityKey(category.name);
            for (const name of category.items) {
                const itemKey = getBreakdownItemIdentityKey(category.name, name);
                if (!itemKey) continue;
                const key = `${categoryKey}::${itemKey}`;
                sceneElementKeys.add(key);
                if (!elements.has(key)) {
                    elements.set(key, { key, itemKey, categoryKey, name, category: category.name });
                }
            }
        }
        elementKeysByScene.set(scene, sceneElementKeys);
    }

    return {
        elements: Array.from(elements.values()),
        elementKeysByScene,
    };
};

export const collectBreakdownElements = (
    scenes: SceneData[],
    includeCategory: (category: BreakdownCategory) => boolean = () => true,
): BreakdownElementIdentity[] => buildBreakdownElementIndex(scenes, includeCategory).elements;

export const sceneHasBreakdownElement = (
    scene: SceneData,
    element: Pick<BreakdownElementIdentity, 'categoryKey' | 'itemKey'>,
): boolean => normalizeBreakdownCategories(scene.categories).some(category =>
    getCategoryIdentityKey(category.name) === element.categoryKey
    && category.items.some(item => getBreakdownItemIdentityKey(category.name, item) === element.itemKey)
);
